import { Button, ErrorMessage, Icon, Label, Modal, Skeleton } from '@common-web';
import { strings } from '@constants/strings';
import { clearTextSelectionDBClick } from '@utils/utils';
import { Flex, Image } from 'antd';
import cx from 'clsx';
import { useState } from 'react';
import { IconPDF, IconWordDoc } from '../../../common/web-view/icons';
import { isPDFBlob, openPdfInNewTab } from '../../../utils/file-uploader-utils';
import FileUpload from '../file-upload';
import { ImageUpload } from '../index';
import styles from './index.module.scss';

const UploadZone = ({
  uploadMultiple = false,
  onChange = () => {},
  value,
  label,
  required = false,
  errorMsg,
  isFirstCover = true,
  uploadBtnText = 'Upload Images',
  showDescription = true,
  descriptors = [
    'Ads with pictures get 5x more views.',
    'Upload good quality pictures with proper lighting.',
    'Double click to set cover image.',
  ],
  associationKey = 'images',
  fileType = 'image',
  uploadMultipleStyles = false,
  loading = false,
  setLoading = () => {},
  disabled = false,
  isSortable = false,
  maxCount = undefined,
  formats = undefined,
  dimensions = null,
  cropable = false,
  btnClassName = null,
  uploadsTab = null,
  dimensionText = null,
  height = 88,
  width = 88,
}) => {
  const [showMore, setShowMore] = useState(false);
  const NUMBER_OF_FILES = 8;

  const customItemRender = (_originNode, file, _fileList, actions, cover = false) => {
    return (
      <>
        {file?.type?.startsWith?.('image/') ? (
          <Image
            onDoubleClick={() => {
              clearTextSelectionDBClick();
              if (isFirstCover) {
                const images = [...value];
                onChange([file, ...images.filter((image) => image.id !== file.id)]);
              }
            }}
            src={file.preview}
            style={{ borderRadius: '5px', objectFit: 'fill' }}
            height={height}
            width={width}
            preview={{
              mask: (
                <Flex
                  gap={8}
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                  justify="space-between"
                  className={styles.imageActions}
                >
                  <Flex align="end">
                    <span
                      style={{
                        backgroundColor: '#000000B2',
                        color: '#fff',
                        fontSize: 12,
                        display: 'inline-block',
                        padding: isFirstCover && cover ? '4px 8px' : '',
                        lineHeight: 1,
                        borderRadius: 4,
                      }}
                    >
                      {isFirstCover && cover && 'Cover'}
                    </span>
                  </Flex>
                  <Flex vertical style={{ height: '100%' }} justify={disabled ? 'flex-end' : 'space-between'}>
                    {!disabled && (
                      <Icon
                        onClick={actions.remove}
                        icon={'MdClear'}
                        hasBackground
                        color={'var(--gray-700)'}
                        bgColor={'var(--gray-200)'}
                        iconSize={'12px'}
                        bgSize="20px"
                      />
                    )}
                    <Icon
                      onClick={actions.preview}
                      icon={'RiExpandDiagonalLine'}
                      hasBackground
                      color="#fff"
                      bgColor={'var(--ant-color-primary)'}
                      iconSize={'12px'}
                      bgSize="20px"
                    />
                  </Flex>
                </Flex>
              ),
            }}
            loading="lazy"
            placeholder={<Skeleton width={width} height={height} borderRadius={6} />}
          />
        ) : (
          <Flex className={styles.docTile} justify="space-between" align="center" gap={8}>
            <Flex gap={8} flex={1}>
              {file?.type === 'application/pdf' ? (
                <IconPDF size={24} className="flexShrink0" />
              ) : (
                <IconWordDoc size={24} className="flexShrink0" />
              )}
              <div
                onClick={() => {
                  isPDFBlob(file?.preview) ? openPdfInNewTab(file?.preview) : window.open(file?.preview, '_blank');
                }}
                className={styles.fileName}
              >
                {file.name}
              </div>
            </Flex>

            {!disabled && (
              <Icon
                onClick={actions.remove}
                icon={'MdClear'}
                hasBackground
                color={'var(--gray-700)'}
                bgColor={'var(--gray-200)'}
                iconSize={'12px'}
                bgSize="20px"
                className="cursor-pointer"
              />
            )}
          </Flex>
        )}
      </>
    );
  };

  return (
    <>
      {label && <Label required={required}>{label}</Label>}
      <Flex
        vertical
        gap={16}
        justify="center"
        className={cx(
          uploadMultiple || uploadMultipleStyles ? styles.uploadZone : styles.uploadZoneSingle,
          value?.length ? styles.imageUploaded : ''
        )}
      >
        {fileType === 'image' ? (
          <>
            <ImageUpload
              dimensions={dimensions}
              uploadMultiple={uploadMultiple}
              onChange={onChange}
              value={value}
              {...(!uploadMultiple && {
                conditionToShowUploadBtn: value.length < 1,
              })}
              showCustomRender={true}
              customItemRender={customItemRender}
              uploadButtonWithList={true}
              isSortable={isSortable}
              uploadBtnText={uploadBtnText}
              showDescription={showDescription}
              descriptors={descriptors}
              associationKey={associationKey}
              setLoading={setLoading}
              loading={loading}
              disabled={disabled}
              maxCount={maxCount}
              cropable={cropable}
              uploadMultipleStyles={uploadMultipleStyles}
              btnClassName={btnClassName}
              uploadsTab={uploadsTab}
              dimensionText={dimensionText}
              height={height}
              width={width}
              formats={formats}
            />

            {value?.length >= NUMBER_OF_FILES && (
              <Flex justify="center">
                <Button
                  onClick={() => {
                    setShowMore(true);
                  }}
                  text={strings.view_all}
                  type={'link'}
                  className={'p-0'}
                  style={{ '--ant-control-height': 'auto' }}
                  icon={'FaChevronDown'}
                  iconSize={12}
                  iconPosition="end"
                />
              </Flex>
            )}
          </>
        ) : (
          <FileUpload
            onChange={onChange}
            value={value}
            showCustomRender={true}
            uploadButtonWithList={true}
            uploadBtnText={uploadBtnText}
            associationKey={associationKey}
            customItemRender={customItemRender}
            {...(!uploadMultiple && {
              conditionToShowUploadBtn: value.length < 1,
            })}
            uploadMultiple={uploadMultiple}
            showDescription={showDescription}
            descriptors={descriptors}
            uploadMultipleStyles={uploadMultipleStyles}
            setLoading={setLoading}
            loading={loading}
            disabled={disabled}
            maxCount={maxCount}
            formats={formats}
          />
        )}
      </Flex>

      <Modal
        open={showMore}
        title={strings.upload_images}
        ModalBody={
          <ImageUpload
            dimensions={dimensions}
            uploadMultiple={uploadMultiple}
            onChange={onChange}
            value={value}
            showMore={showMore}
            setShowMore={setShowMore}
            showCustomRender={true}
            customItemRender={customItemRender}
            uploadButtonWithList={false}
            isSortable={isSortable}
            setLoading={setLoading}
            loading={loading}
            maxCount={maxCount}
            uploadMultipleStyles={uploadMultipleStyles}
            cropable={cropable}
            formats={formats}
          />
        }
        okText={strings.confirm}
        closable
        width={1030}
        handleCancel={() => {
          setShowMore(false);
        }}
      />
      <ErrorMessage errorMsg={errorMsg} />
    </>
  );
};

export default UploadZone;
